import React, { useState } from "react";
import { Modal, Upload, Button, message, Alert, Typography, Select } from "antd";
import { UploadOutlined, FileExcelOutlined, FileTextOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { DocumentType, DocumentStatus } from "~/utils/constants";
import { useCreateFile } from "~/services/file/file.mutations";
import { useGetClients } from "~/services/client/client.queries";
import { ClientStatus } from "~/utils/constants";

const { Text } = Typography;

export const ImportInventoryModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>();
  const createFileMutation = useCreateFile();

  const { data: clientsResponse, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });
  const clients = clientsResponse?.items || [];

  const handleUploadChange = (info: any) => {
    let newFileList = [...info.fileList];
    // Limit to only one file
    newFileList = newFileList.slice(-1);
    
    // Validate file type
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      if (file) {
        const isValidType = 
          file.type === "text/csv" || 
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".csv") ||
          file.name.toLowerCase().endsWith(".pdf");
        
        if (!isValidType) {
          message.error("Please upload a CSV or PDF file");
          newFileList = [];
        } else if (file.size > 10 * 1024 * 1024) {
          message.error("File size must be less than 10MB");
          newFileList = [];
        }
      }
    }
    
    setFileList(newFileList);
  };

  const handleSubmit = async () => {
    if (fileList.length === 0) {
      message.error("Please select a CSV or PDF file to upload");
      return;
    }

    if (!selectedClientId) {
      message.error("Please select a client");
      return;
    }

    setIsUploading(true);

    try {
      const file = fileList[0].originFileObj;
      if (!file) throw new Error("File not found");

      const isPDF = file.name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf";
      const fileTypeLabel = isPDF ? "PDF" : "CSV";

      // Upload file directly using the file service
      const fileData = {
        file: file,
        title: `Inventory Import - ${file.name}`,
        type: DocumentType.INVENTORY,
        status: DocumentStatus.SENT,
        clientId: selectedClientId,
        visibleToStaff: true,
        visibleToClient: false,
      };

      await createFileMutation.mutateAsync(fileData);
      
      message.success(`${fileTypeLabel} file uploaded successfully. It will appear in the Pending Imports list for review.`);
      setFileList([]);
      setSelectedClientId(undefined);
      setOpen(false);
    } catch (error: any) {
      console.error("Error uploading file:", error);
      message.error(error.response?.data?.message || "Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setSelectedClientId(undefined);
    setOpen(false);
  };

  return (
    <>
      <Button
        type="primary"
        onClick={() => setOpen(true)}
        disabled={isUploading}
        className="flex items-center h-11 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm space-x-2 text-base"
      >
        <UploadOutlined />
        <span>Import Inventory</span>
      </Button>
      <Modal
        open={open}
        title="Upload File for Import"
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} disabled={isUploading}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            loading={isUploading}
            disabled={fileList.length === 0 || !selectedClientId}
          >
            Upload for Review
          </Button>,
        ]}
        width={600}
      >
        <div className="space-y-4">
          <Alert
            message="Upload Inventory File"
            description={
              <div className="mt-2">
                <Text className="text-sm">
                  Upload a CSV or PDF file containing inventory data. The file will be added to the Pending Imports list where you can review and import it.
                </Text>
                <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                  <li>Accepted formats: CSV or PDF</li>
                  <li>Maximum file size: 10MB</li>
                  <li>File will appear in Pending Imports for review</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
          />

          <div>
            <Text strong className="text-sm mb-2 block">
              Select Client <span className="text-red-500">*</span>
            </Text>
            <Select
              className="w-full"
              placeholder="Search and select a client..."
              value={selectedClientId}
              onChange={setSelectedClientId}
              loading={clientsLoading}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={clients.map((client) => ({
                value: client.id,
                label:
                  client.companyName ||
                  [client.firstName, client.lastName]
                    .filter(Boolean)
                    .join(" ") ||
                  "Unknown Client",
              }))}
            />
          </div>

          <div>
            <Text strong className="text-sm uppercase tracking-wide mb-2 block">
              Select File
            </Text>
            <Upload
              accept=".csv,.pdf"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={() => false} // Prevent auto-upload
              maxCount={1}
              disabled={isUploading}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={isUploading}
              >
                Choose File (CSV or PDF)
              </Button>
            </Upload>
            {fileList.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                {fileList[0].name?.toLowerCase().endsWith(".pdf") ? (
                  <FileTextOutlined className="text-red-500" />
                ) : (
                  <FileExcelOutlined className="text-green-500" />
                )}
                <Text>{fileList[0].name}</Text>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
